import { observer } from "mobx-react";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import ProductCaseItemsBalanceStore from "../../stores/product-case-items-balance-store";
import RmaNotZeroMoneyAmount from "../rma-not-zero-money-amount/rma-not-zero-money-amount";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import ItemBalanceExchangeRangeAdjustment from "../item-balance-exchange-range-adjustment/item-balance-exchange-range-adjustment";
import ItemBalanceRefundAdjustment from "../item-balance-refund-adjustment/item-balance-refund-adjustment";
import "./rma-product-case-drawer-balance.scss";

interface IProps {
    itemsBalanceStore: ProductCaseItemsBalanceStore;
}

const ProductCaseDrawerBalance: React.FC<IProps> = observer(({ itemsBalanceStore }) => {
    const settingsScope = 'dashboard-settings-store';
    const rmaCardScope = 'dashboard-returns-rma-card';
    const { getTranslation } = useDashboardTranslation();
    return (
        <div className="rma-product-case-drawer-balance-content-container">
            <RmaNotZeroMoneyAmount
                title={getTranslation('RETURNED_ITEM', rmaCardScope)}
                moneyDto={itemsBalanceStore.originalItemPaidPrice}
                amountFontVariant={FONT_VARIANT.BOLD}
            />
            {itemsBalanceStore.isExchange && (
                <RmaNotZeroMoneyAmount
                    title={getTranslation('PRICE_DIFFERENCE_ADJUSTMENTS', rmaCardScope)}
                    subItem={true}
                    moneyDto={itemsBalanceStore.priceDifferenceAdjustments}
                    amountFontVariant={FONT_VARIANT.BOLD}
                    tooltipContent={<div className="rma-product-case-drawer-balance-tooltip-container">
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('RETURN_ITEM_PRICE_ADJUSTED_BASED_ON', rmaCardScope)}:</FontBody2>
                        <>
                            {itemsBalanceStore.originalItemPaidPriceSourceTranslationKey && (
                                <RmaNotZeroMoneyAmount
                                    title={`${getTranslation('ORIGINAL_ITEM', rmaCardScope)} (${getTranslation(itemsBalanceStore.originalItemPaidPriceSourceTranslationKey, settingsScope)})`}
                                    moneyDto={itemsBalanceStore.originalItemPriceForExchange}
                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                    subItem={true}
                                />
                            )}
                            <RmaNotZeroMoneyAmount
                                title={getTranslation('COMPARE_AT_PRICE_DISCOUNT', rmaCardScope)}
                                moneyDto={itemsBalanceStore.compareAtPriceDiscount}
                                color={FONT_COLOR.NEUTRAL_WHITE}
                                subItem={true}
                            />
                        </>
                    </div>}
                />
            )}
            {itemsBalanceStore.exchangeRangeAdjustment && (
                <ItemBalanceExchangeRangeAdjustment
                    exchangeRangeAdjustmentMoneyDto={itemsBalanceStore.exchangeRangeAdjustment}
                    amountFontVariant={FONT_VARIANT.BOLD}
                />
            )}

            {itemsBalanceStore.refundAdjustment && (
                <ItemBalanceRefundAdjustment
                    refundAdjustmentMoneyDto={itemsBalanceStore.refundAdjustment}
                    amountFontVariant={FONT_VARIANT.BOLD}
                    tooltipContent={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        <div className="refund-bonus-tooltip-content">
                            {getTranslation('CUSTOMER_BONUS_TO_REACH_REFUND_BONUS_LIMIT', rmaCardScope)}
                        </div>
                    </FontBody2>}
                />
            )}
            <RmaNotZeroMoneyAmount
                title={getTranslation('BXGY_DISCOUNT_FEE', rmaCardScope)}
                subItem={true}
                moneyDto={itemsBalanceStore.discountFee}
                amountFontVariant={FONT_VARIANT.BOLD}
                tooltipContent={
                    <div className="bxgy-discount-fee-tooltip-content">
                        {
                            itemsBalanceStore.isExchange ? <div className="rma-item-balance-sublines">
                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('RETURN_ITEM_AS_X', rmaCardScope)}:
                                    {getTranslation('BXGY_DESCRIPTION_1', rmaCardScope)}
                                </FontBody2>

                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('RETURN_ITEM_AS_Y', rmaCardScope)}:
                                    {getTranslation('BXGY_DESCRIPTION_2', rmaCardScope)}
                                </FontBody2>
                            </div> :
                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('RETURN_CANCEL_DISCOUNT', rmaCardScope)}</FontBody2>
                        }
                    </div>
                }
            />
            {itemsBalanceStore.isExchange && itemsBalanceStore.newItemPaymentPrice?.amount !== 0 && (
                <>
                    <RmaNotZeroMoneyAmount
                        title={getTranslation('NEW_ITEM', rmaCardScope)}
                        moneyDto={itemsBalanceStore.newItemPaymentPrice}
                        amountFontVariant={FONT_VARIANT.BOLD}
                    />
                    {itemsBalanceStore.newItemPaymentPriceDifferenceAdjustments?.amount !== 0 && <div className="rma-item-balance-sublines">
                        <RmaNotZeroMoneyAmount
                            title={getTranslation('PRICE_DIFFERENCE_ADJUSTMENTS', rmaCardScope)}
                            moneyDto={itemsBalanceStore.newItemPaymentPriceDifferenceAdjustments}
                            amountFontVariant={FONT_VARIANT.BOLD}
                            tooltipContent={
                                <div className="rma-product-case-drawer-balance-tooltip-container">
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('RETURN_ITEM_PRICE_ADJUSTED_BASED_ON', rmaCardScope)}:</FontBody2>
                                    {itemsBalanceStore.newItemPaymentPriceSourceTranslationKey && (
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                        <RmaNotZeroMoneyAmount
                                            title={`${getTranslation('NEW_ITEM', rmaCardScope)} (${getTranslation(itemsBalanceStore.newItemPaymentPriceSourceTranslationKey, settingsScope)})`}
                                            moneyDto={itemsBalanceStore.newItemPaymentPrice}
                                            color={FONT_COLOR.NEUTRAL_WHITE}
                                            useColon={true}
                                            subItem={true}
                                        />
                                    </FontBody2>
                                    )}
                                </div>
                            }
                        />
                    </div>
                    }
                </>
            )}

            {(itemsBalanceStore.showFeesBreakdown) && (
                <>
                    <FontBody2
                        variant={FONT_VARIANT.BOLD}>
                            {getTranslation('FEES', 'dashboard-create-rma')}
                    </FontBody2>
                    <RmaNotZeroMoneyAmount
                        title={getTranslation('RESTOCKING', rmaCardScope)}
                        moneyDto={itemsBalanceStore.restockingFee}
                        amountFontVariant={FONT_VARIANT.BOLD}
                        subItem={true}
                        automationId="item-balance-restocking-fee"
                    />
                    <RmaNotZeroMoneyAmount
                        title={getTranslation('RETURN', rmaCardScope)}
                        moneyDto={itemsBalanceStore.rmaFee}
                        amountFontVariant={FONT_VARIANT.BOLD}
                        subItem={true}
                        automationId="item-balance-return-fee"
                    />
                    <RmaNotZeroMoneyAmount
                        title={getTranslation('RETURN_LABEL', rmaCardScope)}
                        moneyDto={itemsBalanceStore.returnLabelFee}
                        amountFontVariant={FONT_VARIANT.BOLD}
                        subItem={true}
                        automationId="item-balance-return-label-fee"
                    />
                    <RmaNotZeroMoneyAmount
                        title={getTranslation('CANCELLATION', rmaCardScope)}
                        moneyDto={itemsBalanceStore.cancellationFee}
                        amountFontVariant={FONT_VARIANT.BOLD}
                        subItem={true}
                        automationId="item-balance-cancellation-fee"
                    />
                </>

            )}

            {itemsBalanceStore.deductForOtherItems?.map(item =>
                <RmaNotZeroMoneyAmount
                    key={item.otherItemProductCaseId}
                    title={`${getTranslation('DEDUCTED_FOR', rmaCardScope)} ${item.otherItemName}`}
                    moneyDto={item?.totalAmount}
                    amountFontVariant={FONT_VARIANT.BOLD}
                />
            )}
            {itemsBalanceStore.paidByOtherItems?.map(item =>
                <RmaNotZeroMoneyAmount
                    key={item.otherItemProductCaseId}
                    title={`${getTranslation('PAID_BY', rmaCardScope)} ${item.otherItemName}`}
                    moneyDto={item?.totalAmount}
                    amountFontVariant={FONT_VARIANT.BOLD}
                />
            )}
        </div>
    )
});

export default ProductCaseDrawerBalance;
